import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment,
  CircularProgress, Alert, Tooltip, IconButton, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Snackbar, Chip, Stack,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, PowerSettingsNew as PowerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, PlanChip, StatusChip, UsageCell, PLANS, STATUSES, errorMessage } from '../../components/Admin/adminUi';

const AdminCustomers = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [confirm, setConfirm] = useState(null); // company pending activate/deactivate
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.admin.getCompanies();
      setCompanies(res.data.companies || []);
    } catch (err) {
      setError(errorMessage(err, 'Failed to fetch companies'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return companies.filter((c) => {
      if (planFilter !== 'all' && c.subscription?.plan !== planFilter) return false;
      if (statusFilter !== 'all' && c.subscription?.status !== statusFilter) return false;
      if (activeFilter === 'active' && !c.isActive) return false;
      if (activeFilter === 'inactive' && c.isActive) return false;
      if (!q) return true;
      return [c.name, c.domain, c.slug, c.industry, c.adminUser?.email, c.adminUser?.fullName]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [companies, search, planFilter, statusFilter, activeFilter]);

  const toggleActive = async () => {
    const company = confirm;
    setConfirm(null);
    if (!company) return;
    try {
      setBusyId(company.id);
      const res = await apiService.admin.updateCompany(company.id, { isActive: !company.isActive });
      const updated = res.data.company;
      setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, ...updated } : c)));
      setToast(`${company.name} ${company.isActive ? 'deactivated' : 'activated'}`);
    } catch (err) {
      setError(errorMessage(err, 'Failed to update company'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading && companies.length === 0) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  return (
    <Box>
      <AdminPageHeader
        title="Customers"
        subtitle={`${companies.length} tenant${companies.length === 1 ? '' : 's'} on the platform`}
        actions={<Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>}
      />
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth size="small" placeholder="Search by company, domain, industry or admin contact"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <TextField select size="small" label="Plan" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="all">All plans</MenuItem>
              {PLANS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="all">All statuses</MenuItem>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Account" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active only</MenuItem>
              <MenuItem value="inactive">Deactivated only</MenuItem>
            </TextField>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Detections</TableCell>
                  <TableCell align="right">Cases</TableCell>
                  <TableCell>Admin contact</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {companies.length ? 'No companies match the current filters' : 'No companies registered yet'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.map((c) => (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer', opacity: c.isActive ? 1 : 0.6 }} onClick={() => navigate(`/admin/customers/${c.id}`)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[c.domain, c.industry, c.size].filter(Boolean).join(' · ')}
                      </Typography>
                      {!c.isActive && <Chip label="DEACTIVATED" size="small" color="error" variant="outlined" sx={{ mt: 0.5, height: 18, fontSize: 10 }} />}
                    </TableCell>
                    <TableCell>
                      <PlanChip plan={c.subscription?.plan} />
                      {c.subscription?.plan === 'trial' && c.subscription?.trialEndsAt && (
                        <Typography variant="caption" display="block" color="text.secondary">ends {formatDate(c.subscription.trialEndsAt)}</Typography>
                      )}
                    </TableCell>
                    <TableCell><StatusChip status={c.subscription?.status} /></TableCell>
                    <TableCell align="right">
                      <UsageCell used={c.userCount ?? c.usage?.currentUsers} limit={c.limits?.maxUsers} label="Users" />
                      {c.pendingInvites > 0 && <Typography variant="caption" color="text.secondary">{c.pendingInvites} pending</Typography>}
                    </TableCell>
                    <TableCell align="right"><UsageCell used={c.usage?.currentDetections} limit={c.limits?.maxDetections} label="Detections" /></TableCell>
                    <TableCell align="right"><UsageCell used={c.usage?.currentCases} limit={c.limits?.maxCases} label="Cases" /></TableCell>
                    <TableCell>
                      {c.adminUser ? (
                        <>
                          <Typography variant="body2">{c.adminUser.fullName}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{c.adminUser.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last login: {c.adminUser.lastLogin ? formatDateTime(c.adminUser.lastLogin) : 'never'}
                          </Typography>
                        </>
                      ) : <Typography variant="body2" color="text.secondary">No admin user</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="body2" noWrap>{formatDate(c.createdAt)}</Typography></TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={c.isActive ? 'Deactivate company' : 'Activate company'}>
                        <span>
                          <Button
                            size="small" variant="outlined" color={c.isActive ? 'error' : 'success'} startIcon={<PowerIcon />}
                            disabled={busyId === c.id} onClick={() => setConfirm(c)}
                          >
                            {c.isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Showing {filtered.length} of {companies.length} companies
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm?.isActive ? 'Deactivate' : 'Activate'} {confirm?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.isActive
              ? 'Users of this company will no longer be able to sign in or use the API until the company is reactivated.'
              : 'Users of this company will regain access to the platform.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={confirm?.isActive ? 'error' : 'success'} onClick={toggleActive}>
            {confirm?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default AdminCustomers;
